import React, {useState} from 'react'
import {useParams} from 'react-router-dom'
import MemberRepo from '../../../Repo/MemberRepo'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import MemberInvitationDTO from '../../../DTO/MemberInvitationDTO'
import {Nullable} from '../../../../Common/TypeHelper'
import {SignboardDTO} from '../../../DTO/SignboardDTO'
import {validateMemberMailInput} from '../../../Utils/Validations/ValidateMembersMailInput'
import {useAsync} from 'react-use'
import {MBMemberError} from '../../../Common/MBMemberError'
import {CommonResponseStatus} from '../../../../Network/CommonResponse'
import {animateScroll} from 'react-scroll'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import {MessageForAction} from '../../Molecules/BaseMembersPages/MessageForAction'
import {useMBMemberError} from '../../Hooks/UseMBMemberError'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {MailAddressFormContent} from '../../Components/InputContentBlocks/MailAddressFormContent'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {InputErrorSummary} from '../../Molecules/ErrorsAndAlert/ErrorSummary/InputErrorSummary'
import {memberResponseStatusCheck} from '../../../Utils/MemberResponseStatusCheck'
import {useTranslation} from 'react-i18next'

type MembersInvitationProps = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
}

type ErrorState = {
  hasErrors: boolean
  inputErrors: string[]
  errorMessages: string[]
}

export const MembersInvitationPage: React.VFC<MembersInvitationProps> =
  (props): Nullable<JSX.Element> => {
    const {t} = useTranslation('member')
    const {signupKey} = useParams<{ signupKey: string }>()

    const [mail, setMail] = useState('')
    const [mailErrors, setMailErrors] = useState<ErrorState>({
      hasErrors: false,
      inputErrors: [],
      errorMessages: [],
    })
    const [processing, setProcessing] = useState<boolean>(false)

    const throwMBMemberError = useMBMemberError()

    const result = useAsync(async () => {
      return await props.memberRepo.getSignboardBySignupKey(signupKey)
    })

    if (result.loading) {
      return null
    }

    if (result.error) {
      throw new MBMemberError(result.error.message)
    }

    if (!result.value) {
      throw new MBMemberError('UnknownError')
    }

    memberResponseStatusCheck(result.value.status)


    const goToInvitationCompletePage = async (signboard: SignboardDTO): Promise<void> => {
      setProcessing(true)
      setMailErrors({
        hasErrors: false,
        inputErrors: [],
        errorMessages: [],
      })

      const _mail = mail.trim()
      setMail(_mail)

      const validationResult = validateMemberMailInput({mail: _mail})

      if (validationResult.hasError) {
        setMailErrors({
          hasErrors: validationResult.hasError,
          inputErrors: validationResult.errors.mail,
          errorMessages: [],
        })
        setProcessing(false)
        animateScroll.scrollToTop()
        return
      }

      try {
        const resultSendOneTimeUrlMail =
          await props.memberRepo.sendOneTimeUrlMail(new MemberInvitationDTO(signupKey, _mail))

        switch (resultSendOneTimeUrlMail.status) {
          case CommonResponseStatus.Created:
            props.navigator.goToMembersInvitationCompletePage({
              id: signboard?.id || null,
              signupKey: signboard?.signupKey || '',
              shopId: signboard?.shopId || '',
              logoImgUrl: signboard?.logoImgUrl || '',
              signupReturnPageInfo: {
                url: signboard?.signupReturnPageInfo.url || '',
                name: signboard?.signupReturnPageInfo.name || '',
              },
              signupName: signboard?.signupName || '',
              mailMagazineList: signboard?.mailMagazineList || [],
              mainService: signboard.mainService,
              freeServices: signboard.freeServices,
            })
            break
          case CommonResponseStatus.BadRequest:
            setProcessing(false)
            setMailErrors({
              hasErrors: true,
              inputErrors: [],
              errorMessages: resultSendOneTimeUrlMail.errorMessageList,
            })
            break
          case CommonResponseStatus.InternalServerError:
            // noinspection ExceptionCaughtLocallyJS
            throw new MBMemberError('ServerError')
          default:
            // noinspection ExceptionCaughtLocallyJS
            throw new MBMemberError('ServerError')
        }
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : 'SystemError'
        throwMBMemberError(errorMessage)
      }
    }

    if (!result.value.data) {
      throw new MBMemberError('SystemError')
    }

    const signboard = result.value.data

    return (
      <MembersPageTemplate>
        <PageTitle>{`${signboard?.signupName} 新規登録`}</PageTitle>

        <InputErrorSummary display={mailErrors.hasErrors}/>

        <MailAddressFormContent
          title={'本人確認メールアドレス入力'}
          topMessage={
            '新規登録をされる場合、メールによる本人確認が必要です。\nご入力いただいたメールアドレス宛に新規登録ページのURLをお送りします。'}
          note={[
            'ご登録は1人1アカウントとさせていただきます。',
            '1アカウントの複数人での利用、1人で複数アカウントの作成はご遠慮ください。',
            '既に登録されているメールアドレスでは利用登録できません。',
            t('新規登録.本人確認メールアドレス入力.注記 メール受信設定'),
          ]}
          logoImgUrl={signboard?.logoImgUrl}
          inputLabel={'メールアドレス'}
          inputValue={mail}
          hasErrors={mailErrors.hasErrors}
          errorMessages={[...mailErrors.inputErrors, ...mailErrors.errorMessages]}
          onMailAddressChange={((mailAddress): void => setMail(mailAddress))}
          onMailAddressBlur={((mailAddress): void => setMail(mailAddress))}
        />

        <MessageForAction>
          上記の内容に間違いがなければ<br/>
          『送信する』ボタンを押してください。
        </MessageForAction>

        <PIMPageActions>
          <PIMSubmitButton
            testId={'send-button'}
            disabled={processing}
            onClick={(): void => {
              goToInvitationCompletePage(signboard)
            }}
          >
            送信する
          </PIMSubmitButton>
        </PIMPageActions>
      </MembersPageTemplate>
    )
  }
